import { Address } from "@redotech/account-common/address";
import { CustomerPortalRpcClient } from "@redotech/customer-portal-sdk/rpc/client";
import { USA_PROVINCES_BY_CODE } from "@redotech/locale/provinces";
import {
  getLocations,
  getProductsByIds,
} from "@redotech/redo-customer-portal-app/api";
import { CustomerPortalAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  LineItem,
  type OrderWithReturnableItems,
} from "@redotech/redo-order-core-common/order";
import {
  ReturnProduct,
  ReturnableItem,
} from "@redotech/redo-return-core-common/draft-return/draft-return-items";
import { ExchangeGroup } from "@redotech/redo-return-core-common/exchange-group";
import { DiscountDistributionMethod } from "@redotech/redo-return-core-common/exchange-settings";
import { Adjustment } from "@redotech/redo-return-core-common/return-flow";
import { ReturnOptionMethod } from "@redotech/redo-return-core-common/return-flow/return-option";
import type { ReturnTotalsOrder } from "@redotech/redo-return-core-common/return-order-contracts";
import {
  ReturnTotalsCalculator,
  getReturnTotalsFrontendLogFn,
} from "@redotech/redo-return-core-common/return-totals-calculator";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { OriginAddress } from "@redotech/redo-shopify-order-common/shopify-order";
import {
  ShopifyStorefrontClient,
  createCartSimple,
} from "@redotech/shopify-storefront";
import {
  ShopifyIdType,
  convertShopifyIdToGid,
} from "@redotech/shopify-util/id";
import upperCase from "lodash/upperCase";

export const REDO_APP_ID = "3426665";

export const isDevEnvironment = () => process.env.NODE_ENV === "development";

export function optional<T, V>(
  value: T | null | undefined,
  fn: (value: T) => V,
): V | null {
  if (value == null) {
    return null;
  }

  return fn(value);
}

export function getAdjustmentValue(
  adjustment: Adjustment,
  discountPrice: string,
): number {
  const adjustmentValue =
    parseFloat(discountPrice) * (adjustment.proportion - 1.0) + adjustment.flat;

  return adjustmentValue;
}

export function getShowAdjustment(adjustment: Adjustment): boolean {
  return adjustment.flat !== 0 || adjustment.proportion !== 1;
}

/** @returns Whether the adjustment was actually applied earlier (sometimes adjustments are ignored for variant exchanges, etc) */
export function isAdjustmentApplied(
  valueBefore: string | number,
  valueAfter: string | number,
): boolean {
  return Number(valueBefore) !== Number(valueAfter);
}

// TODO Lots of similarities with type Product in ReturnTotalsCalculator - consolidate
export interface APIOutputProduct {
  multipleChoiceAnswers: MultipleChoiceAnswer[];
  order_id: string;
  type: string;
  strategy: string;
  line_item_id: string;
  variant_id: string;
  quantity: number;
  tags: string[];
  returnReason: string;
  price_adjustment: string;
  productId: string;
  price: string;
  sku: string;
  new_item: unknown;
  exchangeGroupItem?: unknown;
  vendor: string;
  isUnbundled: boolean;
}

export function apiOutput(
  item: ReturnProduct,
  priceAdjustment: string,
  order: { _id: string },
  settings: CustomerPortalAppSettings,
): APIOutputProduct {
  return {
    multipleChoiceAnswers: item.productProperties?.multipleChoiceAnswers || [],
    order_id: order._id,
    type: item.productProperties?.green_return
      ? "green_return"
      : item.productProperties?.selected_return_option?.method.type ===
            ReturnOptionMethod.REFUND ||
          item.productProperties?.selected_return_option?.method.type ===
            ReturnOptionMethod.STORE_CREDIT
        ? "return"
        : "exchange",
    strategy: item.productProperties?.green_return
      ? "green_return"
      : item.productProperties?.selected_return_option?.method.type ===
          ReturnOptionMethod.EXCHANGE
        ? settings.exchanges.excessExchangeValue
        : item.productProperties?.selected_return_option?.method.type ===
            ReturnOptionMethod.REFUND
          ? "refund"
          : "store_credit",
    line_item_id: item.id,
    variant_id: "Not implemented",
    quantity: item.quantity || 0,
    tags: item.productTags,
    returnReason: item.productProperties?.returnReason,
    price_adjustment: priceAdjustment,
    productId: item.productId,
    price: item.returnValue,
    sku: item.sku,
    new_item: item.productProperties?.new_item || null,
    exchangeGroupItem: item.productProperties?.exchangeGroupItem || null,
    isUnbundled: item.isUnbundled || false,
    vendor: item.vendor || "",
  };
}

export const extractPriceFromProperties = (lineItem: any, order: any) => {
  let originItemPrice;
  let originItemDiscountPrice;

  if (!order?.isExchangeOrder) {
    return null;
  }
  const originItem =
    order?.originItems?.find(
      (item: any) =>
        item.applicableShopifyOrder === order.shopify.id &&
        item.newVariantId === lineItem.variant_id,
    ) ||
    order?.originItems?.find(
      (item: any) => item.newVariantId === lineItem.variant_id,
    );
  if (originItem) {
    originItemPrice = originItem.preDiscountPrice || originItem.unitPrice;
    originItemDiscountPrice = originItem.unitPrice;
  }
  if (!originItemPrice) {
    const originItem = lineItem.properties?.find(
      ({ name }: { name: string }) => name === "_redo_origin_item",
    );
    originItemPrice =
      originItem?.preDiscountPrice || originItem?.unitPrice || null;
    originItemDiscountPrice = originItem?.unitPrice || null;
  }

  return originItemPrice
    ? {
        original_price: originItemPrice,
        discount_price: originItemDiscountPrice,
      }
    : null;
};

export const hasRedoDiscountCode = (data: any) => {
  return data.shopify.discount_applications?.some((discount: any) =>
    discount.code?.includes("REDO"),
  );
};

export const getItemData = (
  item: any,
  distributionMethod: any,
  order: OrderWithReturnableItems & ReturnTotalsOrder,
  redoCoverageProductIds: ReadonlySet<string>,
  product: any = undefined,
) => {
  const extractedPrice = extractPriceFromProperties(item, order);
  // Won't make api request if it already has for that product.
  // let product = products.find((product) => product.id === item.product_id);
  return {
    ...item,

    processed_at: new Date(order.shopify.processed_at),
    shipping_fees: { store_credit: 0, exchange: 0, refund: 0 },
    coverage: {
      exchange: order.protected && order.coverage.exchange,
      refund: order.protected && order.coverage.refund,
      store_credit: order.protected && order.coverage.storeCredit,
    },
    original_price: item.price,
    discount_price: ReturnTotalsCalculator.getDiscountPriceOld(
      item,
      order,
      [{ error: "Not implemented" }],
      getReturnTotalsFrontendLogFn,
      distributionMethod,
      undefined,
      redoCoverageProductIds,
    ),
    green_return_selected: false,
    green_return_type: "discount", //Default value
    return: null, // This is to define what we do with the product.
    product,
    fixed_strategies: [],
    is_exchange_order: order.isExchangeOrder,
    is_defective: null,
    is_wrong_item: null,
    isManualReview: false,
    isFlagged: false,
    // We use this flag to determine if we should split this single item in a new
    // return. This is used for returns that require approval by the merchant.
    split: false,
    inputAnswers: [], // should be type InputAnswer
    readyToReturn: false, // Set this to true once user has completed flow for item.
    selected_return_option: null,
    new_item: null,
    returnReason: undefined,
    initialLineItem: undefined,
    ...extractedPrice,
  };
};

export function getDistributionMethod(
  settings: CustomerPortalAppSettings,
): DiscountDistributionMethod {
  return (
    settings.discountDistributionMethod || DiscountDistributionMethod.LINE_ITEM
  );
}

// This alters the whole order coming in
export async function makeOrder(
  data: any,
  settings: CustomerPortalAppSettings,
): Promise<OrderWithReturnableItems> {
  const tempData = data;
  const redoCoverageProductIds = new Set(settings.redoCoverageProductIds ?? []);
  const lineItems = data.shopify.line_items.filter(
    (item: any) =>
      !ReturnTotalsCalculator.isRedoItem(item, redoCoverageProductIds),
  );
  const uniqueProductIds = new Set<string>();
  for (const item of lineItems) {
    if (item.product_id != null) {
      uniqueProductIds.add(String(item.product_id));
    }
  }

  let products: any[] = [];
  if (data.provider === "shopify" && uniqueProductIds.size > 0) {
    // One round-trip for the whole order instead of one per unique product.
    // Large orders previously issued 10+ serialized requests here, which
    // blocked the tracking page's initial render.
    const res = await getProductsByIds([...uniqueProductIds]);
    products = Array.isArray(res.data) ? res.data : [];
  }

  // Key by string so lookups work regardless of whether the server returns a
  // numeric id (real product) or a string id (fallback/error path).
  const productsById = new Map<string, any>(
    products.map((p: any) => [String(p.id), p]),
  );

  const distributionMethod = getDistributionMethod(settings);

  const modifiedLineItems = lineItems.map((item: any) => {
    const product = productsById.get(String(item.product_id));

    return getItemData(
      item,
      distributionMethod,
      data,
      redoCoverageProductIds,
      product,
    );
  });
  tempData.line_items = modifiedLineItems;

  if (settings?.exchanges?.blockDiscountsOnExchanges && tempData.shopify) {
    tempData.shopify.discount_codes = [];
  }
  return tempData;
}

export const parseGid = (gid: string) => {
  return /[^/]*$/.exec(gid)![0];
};

//TODO: Make it so only bundle items use bundled types instead of all items
export async function hasReturnableItems(
  order: OrderWithReturnableItems,
  returnType: ReturnTypeEnum,
  rpcClient?: CustomerPortalRpcClient,
): Promise<boolean> {
  if (!rpcClient || !order._id) {
    return false;
  }

  try {
    // Call the RPC method to create draft return and get returnable items
    return await rpcClient.hasReturnableItems({
      returnType,
      orderId: order._id,
    });
  } catch (error) {
    return false;
  }
}

export function listsHaveMatchingElement(
  list1: string[],
  list2: string[],
): boolean {
  return list1.some((key) =>
    list2.some((attribute) => attribute.toLowerCase() === key.toLowerCase()),
  );
}

export function isItemRemovedFromOrder(lineItem: LineItem): boolean {
  return (
    lineItem.fulfillable_quantity === 0 &&
    lineItem.fulfillment_status !== "fulfilled"
  );
}

/**
 * Whether the order has any line item still awaiting fulfillment. Gates on
 * `current_quantity` because a fulfillment hold zeroes `fulfillable_quantity`.
 */
export function hasUnfulfilledItems(order: OrderWithReturnableItems): boolean {
  const lineItems = order.shopify?.line_items ?? [];
  return lineItems.some(
    (lineItem) =>
      (lineItem.current_quantity ?? lineItem.quantity) > 0 &&
      lineItem.fulfillment_status !== "fulfilled",
  );
}

export function getProductsFromReturnableItems(
  returnableItems: ReturnableItem[],
): ReturnableItem["products"] {
  const items: ReturnableItem["products"] = [];
  for (const returnableItem of returnableItems) {
    for (const product of returnableItem.products) {
      items.push(product);
    }
  }

  return items;
}

export const exchangeGroupFilter = (
  product: any,
  exchangeGroup: ExchangeGroup,
  collectionProducts: any = null,
) => {
  if (!exchangeGroup) {
    return true;
  }
  switch (exchangeGroup.type) {
    case "tag:tag":
      return product.tags.some((tag: string) =>
        exchangeGroup.targetTags.includes(tag),
      );
    case "tag:collection":
    case "name:collection":
      return collectionProducts?.some(
        (collectionProduct: any) => collectionProduct.id === product.id,
      );
  }
};

export const isVariantExchange = (item: {
  new_item?: any;
  exchangeGroupItem?: any;
}) => {
  return item.new_item || item.exchangeGroupItem;
};

// FIXME These types are broken - getLocations return a list of LocationData, but LocationData doesn't have fields like country_name, etc
async function getFulfilledFromAddress(
  locationId: string,
  shopifyLocations: { data: { locations: any[] } },
): Promise<Address | null> {
  const response = shopifyLocations || (await getLocations());

  const location = response.data.locations.find(
    (location) => location.id === locationId,
  );
  if (location) {
    return {
      city: location.city,
      country: location.country,
      country_name: location.country_name,
      name: location.name,
      phone: location.phone,
      state: location.province_code,
      street1: location.address1,
      street2: location.address2,
      zip: location.zip,
    };
  } else {
    // No original location
    return null;
  }
}

function getAddress(
  address: Address | undefined,
  originalAddress: Address | undefined | null,
  originalLocation: boolean | undefined,
): Address {
  if (originalLocation && originalAddress) {
    return originalAddress;
  }
  return address as Address;
}

function sameProvince(provinceCode: string, customerProvinceString: string) {
  customerProvinceString = upperCase(customerProvinceString.trim());
  if (provinceCode === customerProvinceString) {
    return true;
  }
  if (
    upperCase(USA_PROVINCES_BY_CODE[provinceCode]) === customerProvinceString
  ) {
    return true;
  }
  return false;
}

interface MultipleChoiceAnswer {
  answer: string;
  questionText: string;
}

function isValidFulfillmentOrigin(
  fulfillmentOrigin: OriginAddress | null | undefined,
): fulfillmentOrigin is NonNullable<OriginAddress> {
  if (!fulfillmentOrigin) {
    return false;
  }

  return !!(
    fulfillmentOrigin.address1 &&
    fulfillmentOrigin.city &&
    fulfillmentOrigin.country_code &&
    fulfillmentOrigin.province_code &&
    fulfillmentOrigin.zip
  );
}

/**
 * Example usage:
 *  array: ["a", "b", "c"], conjunction: "or" => "a, b, or c"
 *  array: ["a", "b"], conjunction: "and" => "a and b"
 *  array: ["a"], conjunction: "and" => "a"
 */
export const arrayToString = (array: string[], conjunction: string): string => {
  switch (array.length) {
    case 0:
      return "";
    case 1:
      return array[0]!;
    case 2:
      return `${array[0]} ${conjunction} ${array[1]}`;
    default: {
      let output = array.slice(0, -1).join(", ");
      output += `, ${conjunction} ${array[array.length - 1]}`;
      return output;
    }
  }
};

export const redirectToWebsite = (
  settings: CustomerPortalAppSettings,
  window: React.MutableRefObject<Window>,
  price?: number,
  selectedProduct?: { url: string },
) => {
  let path = "/apps/redo/exchange-select";
  let delimiter = "?";
  // If they have a custom storefront URL, make sure we send them there.
  if (settings.exchanges.shopSiteURL) {
    path += `?productUrl=${settings.exchanges.shopSiteURL}`;
    delimiter = "&";
  } else if (selectedProduct?.url) {
    path += `?productUrl=${selectedProduct.url}`;
    delimiter = "&";
  }

  if (price) {
    path += `${delimiter}exchangeValue=${price}`;
  }

  // must happen synchronously to avoid popup blockers
  const w = open(
    new URL(
      path,
      `https://${settings.exchanges.shopSiteDomain || settings.brandKit?.customerFacingDomain || settings.storeUrl}`, // THIS DOES NOT HIT LOCAL HOST
    ),
  );
  if (w) {
    w.onunload = () => {
      if (w.closed) {
        // @ts-expect-error: strict-null-checks
        window.current = undefined;
      }
    };
    // save for use by advanced-exchange
    window.current = w;
  }
};

export const getDaysHoursMinutesSecondsFromMs = (milliseconds: number) => {
  const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (milliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  );
  const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);
  return {
    fullFormat: `${days}d ${hours}h ${minutes}m ${seconds}s`,
    highestValueFormat:
      days > 0
        ? `${days} day${days !== 1 ? "s" : ""}`
        : hours > 0
          ? `${hours} hour${hours !== 1 ? "s" : ""}`
          : minutes > 0
            ? `${minutes} minute${minutes !== 1 ? "s" : ""}`
            : `${seconds} second${seconds !== 1 ? "s" : ""}`,
  };
};

export const headlessRedirect = async ({
  discountCode,
  utmSource,
  utmMedium,
  utmContent,
  utmTerm,
  settings,
  window,
  selectedProduct,
}: {
  discountCode?: string;
  utmSource?: string;
  utmMedium?: string;
  utmContent?: string;
  utmTerm?: string;
  settings: CustomerPortalAppSettings;
  window: React.MutableRefObject<Window>;
  selectedProduct?: {
    url: string;
    variantId?: string;
    selectedPlanId?: string;
  };
}): Promise<boolean> => {
  // Check if we have a variant selected
  if (selectedProduct?.variantId && settings.storefrontAccessToken) {
    try {
      // Create a Shopify Storefront client
      const storefrontClient = new ShopifyStorefrontClient(
        settings.storeUrl,
        settings.storefrontAccessToken,
      );

      // Convert variant ID to GID format
      const variantGid = convertShopifyIdToGid(
        selectedProduct.variantId,
        ShopifyIdType.ProductVariant,
      );

      // Create a cart with the selected product
      const cartInput = {
        lines: [
          {
            merchandiseId: variantGid,
            quantity: 1,
            sellingPlanId: selectedProduct.selectedPlanId
              ? convertShopifyIdToGid(
                  selectedProduct.selectedPlanId,
                  ShopifyIdType.SellingPlan,
                )
              : undefined,
          },
        ],
        discountCodes: discountCode ? [discountCode] : undefined,
      };

      // Create the cart and get the checkout URL
      const cart = await createCartSimple(storefrontClient, cartInput);

      if (cart?.checkoutUrl) {
        // Build URL with UTM parameters
        const checkoutUrl = new URL(cart.checkoutUrl);

        if (utmSource) {
          checkoutUrl.searchParams.set("utm_source", utmSource);
        }
        if (utmMedium) {
          checkoutUrl.searchParams.set("utm_medium", utmMedium);
        }
        if (utmContent) {
          checkoutUrl.searchParams.set("utm_content", utmContent);
        }
        if (utmTerm) {
          checkoutUrl.searchParams.set("utm_term", utmTerm);
        }

        // Open the checkout URL
        const w = open(checkoutUrl.toString());
        if (w) {
          w.onunload = () => {
            if (w.closed) {
              // @ts-expect-error: strict-null-checks
              window.current = undefined;
            }
          };
          // save for use by advanced-exchange
          window.current = w;
        }
        return true;
      }
    } catch (error) {
      console.error("Failed to create Shopify checkout:", error);
      return false;
    }
  }
  return false;
};

export const redirectToWebsiteWithDiscountCode = async ({
  discountCode,
  utmSource,
  utmMedium,
  utmContent,
  utmTerm,
  settings,
  window,
  selectedProduct,
}: {
  discountCode?: string;
  utmSource?: string;
  utmMedium?: string;
  utmContent?: string;
  utmTerm?: string;
  settings: CustomerPortalAppSettings;
  window: React.MutableRefObject<Window>;
  selectedProduct?: {
    url: string;
    variantId?: string;
    selectedPlanId?: string;
  };
}) => {
  // Try headless redirect first for supported stores
  const headlessSuccess = await headlessRedirect({
    discountCode,
    utmSource,
    utmMedium,
    utmContent,
    utmTerm,
    settings,
    window,
    selectedProduct,
  });

  if (headlessSuccess) {
    return;
  }

  // Default behavior for other stores or if headless redirect fails
  const redirectUrl = selectedProduct?.variantId
    ? `/cart/${selectedProduct.variantId}:1`
    : discountCode
      ? `/discount/${discountCode}`
      : "/";

  const url = new URL(
    `https://${
      settings.exchanges.shopSiteDomain ||
      settings.brandKit?.customerFacingDomain ||
      settings.storeUrl
    }${redirectUrl}`,
  );
  if (selectedProduct?.url && !selectedProduct?.variantId) {
    url.searchParams.set("productUrl", selectedProduct.url);
  } else if (settings.exchanges.shopSiteURL && !selectedProduct?.variantId) {
    // If they have a custom storefront URL, make sure we send them there.
    url.searchParams.set("productUrl", settings.exchanges.shopSiteURL);
  }

  if (utmSource) {
    url.searchParams.set("utm_source", utmSource);
  }
  if (utmMedium) {
    url.searchParams.set("utm_medium", utmMedium);
  }
  if (utmContent) {
    url.searchParams.set("utm_content", utmContent);
  }
  if (utmTerm) {
    url.searchParams.set("utm_term", utmTerm);
  }
  if (discountCode && selectedProduct?.variantId) {
    url.searchParams.set("discount", discountCode);
  }

  // must happen synchronously to avoid popup blockers
  const w = open(url);
  if (w) {
    w.onunload = () => {
      if (w.closed) {
        // @ts-expect-error: strict-null-checks
        window.current = undefined;
      }
    };
    // save for use by advanced-exchange
    window.current = w;
  }
};
